import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMemberIDCard,
  useMyDepedentAccess,
  useGetDependentAccess,
  useMemberReferralRequest,
} from '@sentara-health/sentara-api-hooks-core';
import { toSentancePascalCase, getDecryptedValue, UserDetails, internalServerErrorCode, CommonErrorMessage, Proxy, backToDashboardLink, getLocalStorageBoolean, ApiWrapper, ANALYTICS_MESSAGES, useAnalytics } from 'common';
import AuthorizationTable from './AuthorizationTable';
import { GlobalContext } from 'context';
import {
  Button,
  Headings,
  PrintIcon,
  Loader,
  Checkbox,
  Table,
  Paragraph,
  ExternalLinkIcon,
  Link
} from '@sentara-health/optimahealth_web';
import { DependentConsentModals } from './DependentConsentModals';
import { useDependentConsent } from './useDependentConsent';
import DependentAccessCheckbox from './DependentAccessCheckbox';
/**
 * T authorizations
 * @returns
 */
const Authorizations: React.FC<any> = () => {
  const { t } = useTranslation();
  const { trackContentListing } = useAnalytics();
  const [membersTable, setMembersTable] = useState<any>([]);
  const [authorizationTable, setAuthorizationTable] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSpouse, setShowSpouse] = useState(false);
  const [showParent, setShowParent] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [mId, setMId] = useState<string | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);
  const { memberIDCardType } = useMemberIDCard();
  const { MyDepedentAccessType } = useMyDepedentAccess();
  const { GetDependentAccessType } = useGetDependentAccess();
  const { getMemberReferralRequest } = useMemberReferralRequest();
  const { data: memberReferrals, callApi: getMemberReferrals }: any =
      ApiWrapper(getMemberReferralRequest);
  const [showData, setShowData] = useState(false);
  const {
    loginMID,
    getFeature,
    ssoEndPoints,
    loginMemberId
  } = useContext(GlobalContext);
  const [errorMessage, showErrorMessage] = useState('');
  const consent = useDependentConsent({
    checkMemberAccess,
    membersTable,
    setLoading,
  });
  const {
    dependentAccess,
    setDependentAccess,
    handleDependentAccessChange,
    openViewTermsModal,
    openErrorModal,
  } = consent;

  function simplifyData(data: any[] = []) {
  return data.map(item => {
    const liability = item?.extension?.supportingInfo?.find((i: any) => i.display === "Liability")?.reference || "";
    const phone = item?.extension?.supportingInfo?.find((i: any) => i.display === "Phone")?.reference || null;
    const status = item?.status?.value || null;
    return {
      isSEP: false,
      authorizationNumber: item?.identifier?.[0]?.value || null,
      memberId: item?.subject?.value || loginMemberId,
      medicaidID: null,
      memberFullName: item?.subject?.display || null,
      effectiveDate: item?.occurrence?.start ? `${item.occurrence.start}T00:00:00Z` : null,
      expirationDate: item?.occurrence?.end ? `${item.occurrence.end}T00:00:00Z` : null,
      referredBy: item?.requester?.display || null,
      liability: liability.length > 0 ? liability : status,
      referredByProviderID: item?.requester?.value || null,
      referredToProviderID: item?.performer?.value || null,
      procedure: item?.procedure?.coding?.[0]?.value || null,
      claimedVisits: item?.extension?.claimedVisits ?? null,
      authorizedVisits: item?.extension?.authorizedVisits ?? null,
      dsuPhone: phone,
      referredToPracticeName: item?.recipient?.display || null,
      referredToProviderName: item?.performer?.display || null,
      status: status,
      mId: selectedMember?.mId || null,
    };
  });
}

  // Function to fetch members data
  async function fetchMembersData() {
    setLoading(true);

    try {
      const result = await memberIDCardType('auth');
      if (result?.data?.members) {
        const members = Array.isArray(result.data.members) ? result.data.members : [];
        setMembersTable(members);

        const response = await GetDependentAccessType();
        setDependentAccess(response?.data?.isDependentAccess || false);

        if (members.length > 0) {
          setMId(members[0].mId);
          setMemberId(members[0].memberId);
          setSelectedMember(members[0]);
          await fetchDependentDetails(members[0].memberId, false);
        }
        if (result?.data?.parentPermission === true) {
          setShowParent(true);
        } else if (result?.data?.spoucePermission === true) {
          setShowSpouse(true);
        }
      } else if (result?.errorCode) {
        // Stop the loader and show the error modal on API failure.
        setLoading(false);
        openErrorModal(result);
      }
    } catch (error) {
      setLoading(false);
      openErrorModal(error);
    }
  }

  // Function to fetch dependent details
  async function fetchDependentDetails(id?: string, showLoader: boolean = true) {
    if (showLoader) {
      setLoading(true);
    }

    // Calculate start date: 18 months ago from today
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 18);
    const formattedStartDate = startDate.toISOString().split('T')[0];

    // Calculate end date: 18 months from today
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + 18);
    const formattedEndDate = endDate.toISOString().split('T')[0];

    let request = {
      identifier: [
        {
          system:'MemberId',
          value: id || selectedMember.memberId || memberId,
        }
      ],
      "period": {
        "start": formattedStartDate,
        "end": formattedEndDate
      }
    }

    await getMemberReferrals(request);
    
  }

  useEffect(() => {
    (async () => {
      await fetchMembersData();
    })();
  }, []);

  useEffect(() => {
    setAuthorizationTable([]);
    if(memberReferrals != null && typeof memberReferrals === 'string' && memberReferrals.trim() === '') {
      setLoading(false);
      setShowData(true);
      
      } else if(memberReferrals?.items.length > 0) {
      const simplifiedReferrals = memberReferrals?.items ? simplifyData(memberReferrals?.items) : [];
      setAuthorizationTable(simplifiedReferrals);
      setLoading(false);
      setShowData(true);
    }
    
  }, [memberReferrals]);

  // Effect to fetch dependent details when memberId changes
  useEffect(() => {
    if (!getLocalStorageBoolean(backToDashboardLink)) {
      setMId(loginMID);
    }
  }, [loginMID]);

  // Effect to fetch dependent details for proxy view
  useEffect(() => {
    (async () => {
      if (membersTable?.length > 0) {
        if (getLocalStorageBoolean(backToDashboardLink)) {
          let personalDetails = await getDecryptedValue(Proxy.memberProxy);
          let personalDetailsList = personalDetails ? JSON.parse(personalDetails) : '';
          const decryptedValue = personalDetailsList.MemberId;
          let filteredMid = membersTable.filter((item: { memberId: string }) => item.memberId === decryptedValue);
          setMemberId(filteredMid[0]?.memberId);
          const MID = filteredMid[0]?.mId;
          if (MID) {
            setMId(MID);
          }
        }
      }
    })();
  }, [membersTable]);
  // Asynchronous function to check member access
  async function checkMemberAccess(value: boolean) {
    const data = await getDecryptedValue(UserDetails.usedId);
    const result: any = await MyDepedentAccessType({
      allowAccess: value,
      userId: data,
    });
    if (result?.errorCode) {
      throw new Error(
        result.errorCode === internalServerErrorCode
          ? t('common.internalServerError') || ''
          : result?.error || ''
      );
    }
  }

  // Function to get member details
  const getMemberDetails = () => {
    (async () => {
      await fetchDependentDetails(undefined, true);
    })();
  };

  // Function to handle print functionality
  const handlePrint = (e: any) => {
    e.preventDefault();
    trackContentListing('1307', ANALYTICS_MESSAGES.AUTHORIZATION_PRINT_CLICK, 1);
    window.print();
  };

  const headerData = [
    { text: <span data-testid="authSelectoneTab">{t('common.selectOne')}</span> },
    { text: <span data-testid="authViewClaimTab">{t('authorizations.viewClaimsfor')}</span> },
    { text: <span data-testid="authMemberIdTab">{t('common.memberId')}</span> }
  ]

  const AuthData = (membersTable: any[]) => {
    return (
      membersTable?.length > 0 &&
      membersTable.map((item: any, i: any) => {
        return [
          <tr key={item.mId}>
            <td>
              <div className="d-flex justify-content-center">
                <Checkbox
                  type="radio"
                  id={`'member${i}'`}
                  dataTestId={`'member${i}'`}
                  name={'members'}
                  value={item.mId}
                  checked={mId === item.mId}
                  onChange={() => {
                    trackContentListing('1305', ANALYTICS_MESSAGES.AUTHORIZATION_MEMBER_SELECT_CLICK, i + 1);
                    setMemberId(item.memberId);
                    setSelectedMember(item);
                    setMId(item.mId);
                  }}
                />
              </div>
            </td>
            <th
              data-testid={`memberName${i}`}
            >
              {toSentancePascalCase(item.memberName)}
            </th>
            <td data-testid={`memberid${i}`}>{item.memberId}</td>
          </tr>,
        ];
      })
    )
  }
  return (
    <>
      {loading && <div className="container-fluid" data-testid="loader"><Loader /></div>}
      <div className="not-print">
        <div className="d-lg-flex d-md-flex not-print align-items-center">
          <div className="mb-3 flex-1">
            <Headings
              level={1}
              text={t('common.authorizations')}
              dataTestId="authorization_header"
              variant="semiBold"
            />
          </div>
          <div className="d-flex align-items-center gap-2 px-2 mb-4">
            <Button
              iconLeft={<PrintIcon fill="primary" />}
              variant="link_btn"
              onClick={handlePrint}
              dataTestId={'print-link'}
            >
              {t('common.print')}
            </Button>
          </div>
        </div>

        {(showSpouse || showParent) && getFeature?.Allowspouseviewclaims ? (
          <DependentAccessCheckbox
            accessType={showSpouse ? 'spouse' : 'parent'}
            label={
              showSpouse
                ? t('authorizations.spouseAllowAccess')
                : t('authorizations.parentAllowAccess')
            }
            dependentAccess={dependentAccess}
            onAccessChange={handleDependentAccessChange}
            onViewTerms={openViewTermsModal}
            viewTermsLabel={t('common.viewTerms')}
            viewTermsDataTestId={showSpouse ? 'spouseViewTermsButton' : 'parentViewTermsButton'}
            showViewTerms={getFeature?.CheckboxPopup === true}
          />
        ) : null}

        {membersTable?.length > 0 && (
          <>
            <div className='table-responsive overflow-auto'>
              <Table className="authorizations-table" header={headerData} rowsChildren={AuthData(membersTable)} />
            </div>
            <div className="flex-display pt-2 mb-4">
              <Button
                dataTestId="viewauthorization"
                onClick={(e: any) => {
                  e.stopPropagation();
                  trackContentListing('1306', ANALYTICS_MESSAGES.AUTHORIZATION_VIEW_CLICK, 1);
                  getMemberDetails();
                }}
                onKeyDown={(e: any) => {
                  e.stopPropagation();
                }}
                variant="primary_btn"
              >
                {t('authorizations.viewauthorization')}
              </Button>
              {getFeature?.ClaimsStaticLink && (
                <div className="d-flex align-items-center ml-3">
                  <Link className="text-decoration-underline small-link" to={ssoEndPoints?.authorizationsLinkUrl} dataTestid="authorizationsLink" target="_blank" rel="noopener noreferrer" onClick={() => trackContentListing('1308', ANALYTICS_MESSAGES.AUTHORIZATION_EXTERNAL_LINK_CLICK, 1)}>{t('authorizations.authorizationsLink')}<span className="ml-4"><ExternalLinkIcon /></span></Link>
                </div>
              )}
            </div>
          </>
        )}

        {/* To show the internalServerError message */}
        <CommonErrorMessage message={errorMessage} />

      </div>
      {showData && (
        <AuthorizationTable
          authorizationTable={authorizationTable}
          setAuthorizationTable={setAuthorizationTable}
          memberId={mId}
        />
      )}
      <DependentConsentModals consent={consent} />
      <div
        className="mb-4 pb-2 pt-2"
        data-testid="authorizationsProcessed"
      >
        <Paragraph text={t('authorizations.authorizationsProcessed')} variant="regular" className="pe-1" />
      </div>
    </>
  );
};
export default Authorizations;